export class ApiErrors {
  private static deduplicateErrors(errors: string[]): string[] {
    const seen = new Map<string, string>();
    
    for (const error of errors) {
      const trimmed = error.trim();
      if (!trimmed) continue;
      
      const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ');
      
      if (!seen.has(normalized)) {
        seen.set(normalized, trimmed);
      }
    }
    
    return Array.from(seen.values());
  }

  static extractAllErrors(
    payload: any,
    response?: Response,
    fallbackMessage?: string
  ): string[] {
    const isSuccessful = response && (response.ok || response.status === 200 || response.status === 201 || response.status === 204);
    
    if (!payload) {
      if (isSuccessful) {
        return [];
      }
      if (response?.statusText && response.statusText !== 'OK' && response.statusText !== 'Created' && response.statusText !== 'No Content') {
        return [response.statusText];
      }
      return fallbackMessage ? [fallbackMessage] : ['Unknown error'];
    }

    const errors: string[] = [];
    const seenMessages = new Set<string>();

    const extractMessage = (item: any): string | null => {
      if (!item) return null;
      
      if (typeof item === 'string') {
        const trimmed = item.trim();
        return trimmed || null;
      }
      
      if (typeof item === 'number' || typeof item === 'boolean') {
        return String(item);
      }
      
      if (typeof item === 'object') {
        const msg = 
          item.message || item.Message || 
          item.details || item.Details || 
          item.error || item.Error ||
          item.errorMessage || item.ErrorMessage ||
          item.error_description || item.errorDescription ||
          item.title || item.Title ||
          item.text || item.Text;
          
        if (msg && typeof msg === 'string') {
          const trimmed = msg.trim();
          return trimmed || null;
        }
        
        if (item.fieldName && item.errorMessage) {
          return `${item.fieldName}: ${item.errorMessage}`;
        }
        if (item.propertyName && item.errorMessage) {
          return `${item.propertyName}: ${item.errorMessage}`;
        }
        if (item.key && item.value) {
          return `${item.key}: ${item.value}`;
        }
        
        if (Object.keys(item).length > 0) {
          for (const key of Object.keys(item)) {
            const value = item[key];
            if (typeof value === 'string' && value.trim().length > 0) {
              const lowerKey = key.toLowerCase();
              if (lowerKey.includes('error') || lowerKey.includes('message') || lowerKey.includes('detail')) {
                return value.trim();
              }
            }
          }
          try {
            const str = JSON.stringify(item);
            if (str.length < 200 && (str.includes('error') || str.includes('Error') || str.includes('message'))) {
              return str;
            }
          } catch {
            return null;
          }
        }
      }
      
      return null;
    };

    const addUniqueError = (msg: string | null) => {
      if (!msg) return;
      const trimmed = msg.trim();
      if (!trimmed) return;
      const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ');
      if (!seenMessages.has(normalized)) {
        seenMessages.add(normalized);
        errors.push(trimmed);
      }
    };

    let parsed: any = payload;
    if (payload instanceof Response) {
      if (response?.statusText && response.statusText !== 'OK') {
        errors.push(response.statusText);
      }
      return errors.length > 0 ? errors : (fallbackMessage ? [fallbackMessage] : ['Unknown error']);
    }

    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (trimmed) {
        try {
          parsed = JSON.parse(trimmed);
        } catch {
          if (isSuccessful) {
            return [];
          }
          return [trimmed];
        }
      } else {
        if (isSuccessful) {
          return [];
        }
        return fallbackMessage ? [fallbackMessage] : ['Unknown error'];
      }
    }

    const errorsArray = 
      parsed?.errors || 
      parsed?.Errors || 
      parsed?.error || 
      parsed?.Error ||
      parsed?.validationErrors ||
      parsed?.ValidationErrors ||
      parsed?.validation_errors ||
      parsed?.Validation_Errors ||
      parsed?.errorMessages ||
      parsed?.ErrorMessages ||
      parsed?.error_messages ||
      parsed?.Error_Messages ||
      (Array.isArray(parsed?.error) ? parsed.error : null);

    if (Array.isArray(errorsArray) && errorsArray.length > 0) {
      for (const errorItem of errorsArray) {
        const msg = extractMessage(errorItem);
        if (msg) {
          addUniqueError(msg);
        }
        
        if (errorItem && typeof errorItem === 'object') {
          const nestedReasons = errorItem.reasons || errorItem.Reasons || errorItem.reason || errorItem.Reason;
          if (Array.isArray(nestedReasons) && nestedReasons.length > 0) {
            for (const reason of nestedReasons) {
              const reasonMsg = extractMessage(reason);
              if (reasonMsg) {
                addUniqueError(reasonMsg);
              }
            }
          }
        }
      }
    }

    const reasonsArray = 
      parsed?.reasons || 
      parsed?.Reasons ||
      parsed?.reason ||
      parsed?.Reason;
    if (Array.isArray(reasonsArray) && reasonsArray.length > 0) {
      for (const reason of reasonsArray) {
        const msg = extractMessage(reason);
        if (msg) {
          addUniqueError(msg);
        }
      }
    }

    const modelState = parsed?.modelState || parsed?.ModelState || parsed?.model_state || parsed?.Model_State;
    if (modelState && typeof modelState === 'object' && !Array.isArray(modelState)) {
      for (const key in modelState) {
        if (modelState.hasOwnProperty(key)) {
          const fieldErrors = modelState[key];
          if (Array.isArray(fieldErrors)) {
            for (const fieldError of fieldErrors) {
              const msg = extractMessage(fieldError);
              if (msg) {
                addUniqueError(msg);
              }
            }
          } else if (typeof fieldErrors === 'string') {
            const trimmed = fieldErrors.trim();
            if (trimmed) {
              addUniqueError(trimmed);
            }
          }
        }
      }
    }

    const validationErrors = parsed?.validationErrors || parsed?.ValidationErrors || parsed?.validation_errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      for (const item of validationErrors) {
        if (typeof item === 'string') {
          addUniqueError(item.trim());
        } else if (item?.errorMessage) {
          addUniqueError(item.errorMessage.trim());
        } else if (item?.message) {
          addUniqueError(item.message.trim());
        } else if (item?.fieldName && item?.errorMessage) {
          addUniqueError(`${item.fieldName}: ${item.errorMessage}`.trim());
        }
      }
    }

    if (Array.isArray(parsed) && parsed.length > 0) {
      for (const item of parsed) {
        const msg = extractMessage(item);
        if (msg) {
          addUniqueError(msg);
        }
      }
    }

    const singleMessage = parsed?.message || parsed?.Message || parsed?.errorMessage || parsed?.ErrorMessage;
    if (singleMessage && typeof singleMessage === 'string') {
      const trimmed = singleMessage.trim();
      if (trimmed) {
        addUniqueError(trimmed);
      }
    }

    const title = parsed?.title || parsed?.Title;
    if (title && typeof title === 'string') {
      const trimmed = title.trim();
      if (trimmed) {
        addUniqueError(trimmed);
      }
    }

    if (parsed?.error?.message) {
      const nestedMsg = extractMessage(parsed.error);
      if (nestedMsg) {
        addUniqueError(nestedMsg);
      }
    }
    
    if (parsed?.data?.errors) {
      const nestedErrors = this.extractAllErrors(parsed.data.errors);
      for (const err of nestedErrors) {
        addUniqueError(err);
      }
    }
    
    if (parsed?.response?.data) {
      const responseErrors = this.extractAllErrors(parsed.response.data);
      for (const err of responseErrors) {
        addUniqueError(err);
      }
    }

    const detailOrDetails = parsed?.detail || parsed?.Detail || parsed?.details || parsed?.Details;
    if (detailOrDetails) {
      const detail = extractMessage(detailOrDetails);
      if (detail) {
        addUniqueError(detail);
      }
    }

    if (errors.length > 0) {
      return this.deduplicateErrors(errors);
    }

    if (parsed?.data) {
      const dataErrors = this.extractAllErrors(parsed.data);
      if (dataErrors.length > 0) {
        return dataErrors;
      }
    }

    if (isSuccessful) {
      return [];
    }

    if (response?.statusText && response.statusText !== 'OK' && response.statusText !== 'Created' && response.statusText !== 'No Content') {
      return [response.statusText];
    }

    return fallbackMessage ? [fallbackMessage] : ['Unknown error'];
  }

  static async extractErrorsFromResponse(
    response: Response,
    fallbackMessage?: string
  ): Promise<string[]> {
    const isSuccessful = response.ok || response.status === 200 || response.status === 201 || response.status === 204;
    
    let rawText = '';
    try {
      rawText = await response.text();
    } catch {
      rawText = '';
    }

    if (!rawText) {
      if (isSuccessful) {
        return [];
      }
      if (response.statusText && response.statusText !== 'OK' && response.statusText !== 'Created' && response.statusText !== 'No Content') {
        return [response.statusText];
      }
      return fallbackMessage ? [fallbackMessage] : ['Unknown error'];
    }

    let parsed: any = null;
    try {
      parsed = JSON.parse(rawText);
    } catch {
      if (isSuccessful) {
        return [];
      }
      return [rawText.trim() || fallbackMessage || 'Unknown error'];
    }

    return this.extractAllErrors(parsed, response, fallbackMessage);
  }

  static formatErrors(errors: string[], separator: string = '; '): string {
    if (!errors || errors.length === 0) {
      return '';
    }
    const unique = this.deduplicateErrors(errors);
    return unique.join(separator);
  }

  static extractSingleError(
    payload: any,
    response?: Response,
    fallbackMessage?: string
  ): string {
    const errors = this.extractAllErrors(payload, response, fallbackMessage);
    return errors.length > 0 ? errors[0] : (fallbackMessage || 'Unknown error');
  }
}